import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import axios from 'axios';

import { debounce, formatDate, formatDateWithGmt } from '@src/utils';
import { useCountries, useRegistrationCheck, useRoomQuery } from '@src/hooks/queries/useWebinarQuery';
import { isWebView } from '@src/helpers';

import { EWebinarRoutes } from '@src/constants';

type TFormData = {
  firstName: string;
  // lastName: string;
  email: string;
  company: string;
  country: string;
  city: string;
  countryCode: string;
};

const namePattern = /^[a-zA-Z ]+$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useRegistrationForm = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roomId = params.get('wid');

  const { data: roomData } = useRoomQuery(roomId);
  const { data: countries = [] } = useCountries();
  const { data: { isEnabled: isRegistrationEnabled, code: responseCode } = {}, isLoading: isValidating } =
    useRegistrationCheck(roomId);

  const [formData, setFormData] = useState<TFormData>({
    firstName: '',
    email: '',
    company: '',
    country: '',
    city: '',
    countryCode: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const setSelectedCountry = (code: string) => {
    const selected = countries.find((c) => c.value === code);
    setFormData((prev) => ({
      ...prev,
      country: selected?.label || '',
      countryCode: code,
    }));
  };

  useEffect(() => {
    const now = new Date();
    const webinarStart = roomData?.startTime ? new Date(roomData.startTime) : null;

    if (!isValidating && !isRegistrationEnabled && roomData?.roomTitle) {
      if (webinarStart && now < webinarStart) {
        navigate(`/webinar/${roomId}/full-registration`);
      } else if (webinarStart && now >= webinarStart) {
        navigate(`/webinar/closed/${roomData.roomTitle}`);
      }
    } else if (!isValidating && isRegistrationEnabled && responseCode === 'REGISTRATION_NOT_REQUIRED') {
      // Only add title parameter if in WebView
      const titleParam = isWebView() && roomData?.roomTitle ? `&title=${encodeURIComponent(roomData.roomTitle)}` : '';
      navigate(`${EWebinarRoutes.JOIN}?wid=${roomData?.id}${titleParam}`);
    }
  }, [isRegistrationEnabled, isValidating, roomData, navigate, responseCode, roomId]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (!namePattern.test(formData.firstName.trim())) {
      newErrors.firstName = 'Only letters and spaces are allowed.';
    } else if (formData.firstName.length < 2 || formData.firstName.length > 30) {
      newErrors.firstName = 'The name must contain 2 to 30 characters.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailPattern.test(formData.email.trim())) {
      newErrors.email = 'Email format is invalid';
    }

    return newErrors;
  }, [formData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const apiCall = useCallback(async () => {
    try {
      const payload = { ...formData };

      await axios.post(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/user/register?roomId=${roomId}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      setIsLoading(false);

      navigate(EWebinarRoutes.SUCCESS, {
        state: { roomData },
      });
    } catch (error: any) {
      setIsLoading(false);

      if (error.response?.data?.code === 'REGISTRATION_CLOSED') {
        navigate(`/webinar/${roomId}/full-registration`);
      } else {
        navigate(EWebinarRoutes.ALREADY_REGISTERED, {
          state: { roomData },
        });
      }
    }
  }, [roomId, formData, navigate, roomData]);

  const debouncedApi = useMemo(() => debounce(apiCall, 500), [apiCall]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      debouncedApi.cancel();
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        return;
      }
      setIsLoading(true);
      await debouncedApi();
      setFormErrors({});
    },
    [debouncedApi, validateForm]
  );

  return {
    countries,
    formData,
    formErrors,
    handleChange,
    handleSubmit,
    setSelectedCountry,
    roomData,
    formatDate,
    formatDateWithGmt,
    isLoading,
    isValidating,
    isRegistrationEnabled,
  };
};

export default useRegistrationForm;
